<!-- 
Listing of system logs
-->
<template>
  <div>
    <v-card :class="embedded ? 'v-card--flat' : 'home'">
      <our-table-header
        :listing="listing"
        :search-filters="searchFilters"
        :criteria-mutation="criteriaMutation"
        :expand-multiple="expandMultiple" 
        :has-export="hasExport"
        :no-data-text="noDataText"
        has-expanders
        @update:criteria="refresh"
        @update:pageNumber="updatePageNumber" 
        @click:refresh="refresh" 
        @click:export="exportToExcel" 
        @click:expandAll="expandAll"
        @click:collapseAll="collapseAll" 
        @click:toggleMultiple="toggleMultiple"
      />
      <our-table-busy-indicator :show="listing.loading"/>
      <v-data-table
        :headers="headers" 
        :items="listing.data" 
        :pagination="listing.paging"
        :total-items="listing.paging.totalItems" 
        :rows-per-page-items="rowsPerPageItems"
        class="table-expandable" 
        must-sort hide-actions
        @update:pagination="updatePagination"
      >
        <template slot="items" slot-scope="props">
          <tr :class="[(canExpand(props.item) ? '': 'noselect')]" @click="expandItem(props.index)">
            <our-nav-col :row="props.item.name"/>
            <td>{{ props.item.date | compactDateTime }}</td>
            <td>{{ lookups.systemLogActionDescriptions[props.item.action] }}</td>
            <td v-if="!simple">{{ props.item.description }}</td>
            <td v-if="!hideUserColumn">{{ props.item.userName }}</td>
            <td class="expand-icon">
              <v-icon v-if="canExpand(props.item)" :class="['body-1 ' + (listing.expanded[props.index] ? 'expanded' : '')]">keyboard_arrow_down</v-icon>
            </td>
          </tr>
          <our-expander-row :colspan="7" :expanded="listing.expanded[props.index]">
            <v-card 
              v-if="listing.expanded[props.index] && canExpand(props.item)" 
              flat class="grey lighten-5"
            >
              <v-card-text class="pb-2">
                <dl v-if="props.item.detail" class="wide">
                  <template v-for="(line, i) of props.item.detail">
                    <dt :key="`${props.item.id}-${i}-t`">{{ line.field }}</dt>
                    <dd v-if="line.value" :key="`${props.item.id}-${i}-d`">
                      <pre v-if="isMultiLine(line.value)" class="wrap highlight">{{ line.value }}</pre>
                      <template v-else>{{ line.value }}</template>
                    </dd>
                    <dd v-else :key="`${props.item.id}-${i}-d`">
                      <template v-for="(diff, j) of describeDifferences(line.before, line.after)">
                        <vue-code-diff 
                          v-if="isMultiLine(diff)" 
                          :key="`${props.item.id}-${i}-${j}`" 
                          :old-string="(line.before ? line.before : '')" 
                          :new-string="(line.after ? line.after : '')"
                        />
                        <p v-else :key="`${props.item.id}-${i}-${j}`" class="ma-0 pa-0">
                          {{ diff }}
                        </p>
                      </template>
                    </dd>
                  </template>
                </dl>
                <!-- <pre v-if="props.item.detailText" class="wrap highlight">{{ props.item.detailText }}</pre> -->
                <our-multiline-text v-if="props.item.detailText" :text="props.item.detailText"/>
                <pre v-if="props.item.detailXml" class="wrap highlight">{{ props.item.detailXml }}</pre>
              </v-card-text>
              <v-card-actions>
                <v-menu v-if="getLinks(props.item).length > 0" offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn flat small color="default" v-on="on">
                      Open 
                      <v-icon small class="ml-2">expand_more</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-tile v-for="(link, index) in getLinks(props.item)" :key="index" @click="goTo(link)">
                      <v-list-tile-title>
                        {{ link.description }}
                      </v-list-tile-title>
                    </v-list-tile>
                    <v-divider />
                    <v-list-tile v-for="(link, index) in getLinksWithAPage(props.item)" :key="index+10" :to="link.url" target="_blank">
                      <v-list-tile-title>
                        {{ link.description }} (in new window)
                        <v-icon small class="ml-2">open_in_new</v-icon>
                      </v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </v-card-actions>
            </v-card>
          </our-expander-row>
        </template>
      </v-data-table>
      <our-table-footer :paging="listing.paging" @update:pageNumber="updatePageNumber" @update:rowsPerPage="updateRowsPerPage" />
      <notice-view-dialog v-model="showNoticeViewDialog" :notice-id="currentNoticeId" />
    </v-card>
  </div>
</template>

<script>
import * as action from "@/store/system-logs/action-types";
import * as mutation from "@/store/system-logs/mutation-types";
import NoticeViewDialog from "@/components/notices/NoticeViewDialog.vue";
import tableMixins from "@/mixins/table.mixins";
import historyMixins from "@/mixins/history.mixins";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import VueCodeDiff from "vue-code-diff";
import { SYSTEM_LOGS_EXPORT } from "@/store/claim-types";
import { getSearchFilters } from "@/store/system-logs/helpers";

export default {
  components: { VueCodeDiff, NoticeViewDialog },
  mixins: [tableMixins, historyMixins, filterMixins, lookupMixins],
  props: {
    endpoint: {
      type: String,
      default: '/api/system-logs'
    },
    exportValidateEndpoint: {
      type: String,
      default: '/api/exports/validate/system-logs'
    },
    exportEndpoint: {
      type: String,
      default: '/api/exports/system-logs'
    },
    embedded: {
      type: Boolean,
      default: false
    },
    noticeId: {
      type: Number,
      default: null
    },
    disable: {
      type: Boolean,
      default: false
    },
    simple: {
      type: Boolean,
      default: false
    },
    hideUserColumn: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      action: action.LIST,
      exportAction: action.EXPORT,
      criteriaMutation: mutation.LIST_SET_CRITERIA,
      pagingMutation: mutation.LIST_SET_PAGING,
      expandMutation: mutation.LIST_TOGGLE_EXPAND,
      showNoticeViewDialog: false,
      currentNoticeId: null
    };
  },
  computed: {
    headers() {
      const h = [
        { text: "", value: "Id", class: "navCol", width: 0, sortable: false },
        { text: "Date", value: "Date", sortable: this.canSort, width: 200 },
        { text: "Action", value: "Action", sortable: this.canSort }
      ];
      if (!this.simple) {
        h.push({ text: "Description", value: "Description", sortable: false });
      }
      if (!this.hideUserColumn) {
        h.push({ text: "User", value: "User.Name", sortable: this.canSort });
      }
      h.push({ text: "", value: "Detail", width: 5, sortable: false });
      return h;
    },
    listing() {
      return this.$store.state.systemLogs.listing;
    },
    searchFilters() {
      return getSearchFilters();
    },
    criteria() {
      return this.listing.criteria;
    },
    loaded() {
      return this.listing.loaded;
    },
    hasExport() {
      return this.$store.getters.claims.includes(SYSTEM_LOGS_EXPORT);
    }
  },
  watch: {
    loaded() {
      this.reload();
    }
  },
  mounted() {
    if (this.listing.keepCriteria) {
      this.$store.commit(mutation.LIST_KEEP_CRITERIA, false);
    } else {
      this.$store.commit(mutation.LIST_CLEAR_CRITERIA);
    }
    this.reload();
  },
  methods: {
    getActionPayload() {
      return { endpoint: this.endpoint };
    },
    getExportActionPayload() {
      return { exportEndpoint: this.exportEndpoint, exportValidateEndpoint: this.exportValidateEndpoint };
    },
    reload() {
      if (!this.disable) {
          this.populate();
      }
    },
    canExpand(log) {
      return log.detail || log.detailXml || log.detailText || this.hasLink(log);
    },
    hasLink(log) {
      return this.getLinks(log).length > 0;
    }
  }
};
</script>
